import type { CurrentUser } from '@/auth/permissions'
import { canOpenPath, NAVIGATION } from '@/app/navigation'
import { request } from '@/lib/api'

/**
 * Ассистент по данным CRM (тикет #136).
 *
 * Сервер разбирает вопрос в действие из закрытого списка, исполняет его от имени спрашивающего и
 * отдаёт ответ с записями. Клиент только показывает: что понято, ответ, ссылки на карточки — и
 * отказ словами, а не пустым списком.
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт в JSON пустые поля вовсе
 * (`non_null`).
 */

export type AssistantKind = 'answer' | 'empty' | 'refusal' | 'clarification' | 'unsupported'

export interface AssistantItem {
  label: string
  /** Ключ каталога списков карточки (`sales.deal`). */
  entity: string
  id: string
  title?: string | null
  subtitle?: string | null
  /** Модель сослалась на запись в тексте ответа. */
  cited?: boolean | null
}

export interface AssistantReply {
  id: string
  sessionId: string
  question: string
  at: string
  kind: AssistantKind
  /** Код отказа: `assistant.employee_out_of_scope`, `report.forbidden_source`… */
  code?: string | null
  text: string
  tool?: string | null
  intent?: { tool: string; params?: Record<string, unknown> | null } | null
  items?: AssistantItem[] | null
  table?: { columns: string[]; rows: unknown[][] } | null
  /** Метки, которые модель назвала, а сервер не передавал, — вырезаны из текста. */
  droppedLabels?: string[] | null
  candidates?: string[] | null
  note?: string | null
}

export function askAssistant(question: string, sessionId: string | null): Promise<AssistantReply> {
  return request<AssistantReply>('/ai/assistant/ask', {
    method: 'POST',
    body: sessionId ? { question, sessionId } : { question },
  })
}

export function fetchAssistantHistory(sessionId: string, signal?: AbortSignal): Promise<AssistantReply[]> {
  return request<{ messages: AssistantReply[] }>(`/ai/assistant/sessions/${sessionId}`, { signal })
    .then((body) => body.messages ?? [])
}

/** Путь карточки записи; `null` — раздела у пользователя нет (сервер её и не отдал бы). */
export function itemPath(item: AssistantItem, user: CurrentUser | null): string | null {
  const nav = NAVIGATION.flatMap((section) => section.items)
    .find((candidate) => candidate.listEntity === item.entity)
  if (!nav || !canOpenPath(user, nav.path)) return null
  return `${nav.path}/${item.id}`
}

/** Текст ответа, разрезанный на куски и метки `[S1]` — метка рисуется ссылкой. */
export function splitLabels(text: string): Array<{ text: string } | { label: string }> {
  const parts: Array<{ text: string } | { label: string }> = []
  const pattern = /\[(S\d+)]/g
  let last = 0
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0
    if (index > last) parts.push({ text: text.slice(last, index) })
    parts.push({ label: match[1] ?? '' })
    last = index + match[0].length
  }
  if (last < text.length) parts.push({ text: text.slice(last) })
  return parts
}
